import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, switchMap, map, finalize, shareReplay } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { AuthenticationService } from 'app/original-services/authentication.service';
import { CompanyService } from 'app/original-services/company.service';
import { StorageService } from 'app/original-services/storage.service';
import { LoggerService } from 'app/original-services/logger.service';
import { MCP_SERVER_ADDON_FLAG } from 'app/common/constants';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private featureFlagsSubject = new BehaviorSubject<any>({});
  public featureFlags$ = this.featureFlagsSubject.asObservable();
  // Emits true once a load attempt has completed (success or failure). Remains false if load is skipped.
  private flagsLoadedSubject = new BehaviorSubject<boolean>(false);
  public flagsLoaded$ = this.flagsLoadedSubject.asObservable();
  // Emits true once a subscription load attempt has completed (success or failure with empty list). Remains false if load is skipped.
  private addonsLoadedSubject = new BehaviorSubject<boolean>(false);
  public addonsLoaded$ = this.addonsLoadedSubject.asObservable();
  private isLoggedIn = false;
  private authService: AuthenticationService;
  private companyService: CompanyService;
  private storageService: StorageService;
  private logger: LoggerService;
  // Cached list of active addon SKUs from the subscription
  private activeAddons: string[] = [];
  private featureFlagsLoad$: Observable<any> | null = null;
  private featureFlagsLoadCompanyId: number | null = null;

  private syncLoginFromToken(): void {
    if (this.storageService?.accessToken) {
      this.isLoggedIn = true;
    }
  }

  // Expose a cheap check for active addon SKUs (filled from /subscription/)
  hasActiveAddonSku(sku: string): boolean {
    return !!sku && this.activeAddons.includes(sku);
  }

  hasAnyActiveAddonSku(skus: string[] = []): boolean {
    return Array.isArray(skus) && skus.some(s => this.activeAddons.includes(s));
  }

  constructor(
    private http: HttpClient,
    private injector: Injector
  ) {
    // Get AuthenticationService, CompanyService, StorageService, and LoggerService lazily to avoid circular dependency
    setTimeout(() => {
      this.authService = this.injector.get(AuthenticationService);
      this.companyService = this.injector.get(CompanyService);
      this.storageService = this.injector.get(StorageService);
      try {
        this.logger = this.injector.get(LoggerService);
      } catch (_) {
        // LoggerService might not be available yet, that's okay
      }

      // Subscribe to login events
      this.authService.afterLogin$.subscribe(() => {
        this.isLoggedIn = true;
        this.flagsLoadedSubject.next(false);
        this.addonsLoadedSubject.next(false);
        this.loadFeatureFlags().subscribe();
      });

      // Subscribe to logout events
      this.authService.beforeLogout$.subscribe(() => {
        this.isLoggedIn = false;
        // Clear feature flags when user logs out
        this.featureFlagsSubject.next({});
        this.flagsLoadedSubject.next(false);
        this.addonsLoadedSubject.next(false);
        this.activeAddons = [];
      });

      // Subscribe to company change events
      this.companyService.currentCompanyChanged$.subscribe((value) => {
        if (value && this.isLoggedIn) {
          if (!this.logger) {
            try {
              this.logger = this.injector.get(LoggerService);
            } catch (_) {
              // LoggerService might not be available yet, that's okay
            }
          }
          if (this.logger) {
            this.logger.log('Company changed, reloading feature flags');
          }
          this.flagsLoadedSubject.next(false);
          this.addonsLoadedSubject.next(false);
          this.loadFeatureFlags().subscribe();
        }
      });
    });
  }

  /**
   * Initialize feature flags when the application starts
   * This is used by APP_INITIALIZER to load feature flags on application refresh
   */
  initFeatureFlags(): Promise<any> {
    return new Promise((resolve) => {
      // Ensure dependencies are available synchronously during app init
      try {
        if (!this.storageService) {
          this.storageService = this.injector.get(StorageService);
        }
      } catch (_) {
        // ignore, will resolve as not logged in
      }

      setTimeout(() => {
        // Check if user is logged in by checking for access token
        if (this.storageService?.accessToken) {
          this.syncLoginFromToken();
          this.loadFeatureFlags().subscribe({
            next: () => resolve(true),
            error: () => resolve(false)
          });
        } else {
          resolve(false);
        }
      });
    });
  }

  loadFeatureFlags(): Observable<any> {
    this.syncLoginFromToken();
    const companyId = this.storageService?.companyId;
    // Only load feature flags if user is logged in and companyId is available
    if (!this.isLoggedIn || !companyId) {
      // Do NOT mark as loaded here, we want callers to be able to wait until company becomes available
      return of({});
    }

    if (!this.featureFlagsLoad$ || this.featureFlagsLoadCompanyId !== companyId) {
      this.featureFlagsLoadCompanyId = companyId;
      this.featureFlagsLoad$ = this.http.get<any>(
        `${environment.apiUrl}/companies/${companyId}/feature-flags/`,
      ).pipe(
        switchMap(flags => {
          this.featureFlagsSubject.next(flags);
          this.flagsLoadedSubject.next(true);

          // Load active add-ons and chain it properly to ensure proper cleanup
          return this.http.get<any>(`${environment.apiUrl}/subscription/`).pipe(
            tap(sub => {
              const addons: string[] = Array.isArray(sub?.addons) ? sub.addons : [];
              this.activeAddons = addons.filter(Boolean);
              this.addonsLoadedSubject.next(true);
            }),
            catchError(err => {
              console.warn('Error loading subscription for addons, continuing without addon cache.', err);
              this.activeAddons = [];
              this.addonsLoadedSubject.next(true);
              return of(null);
            }),
            map(() => flags),
          );
        }),
        catchError(error => {
          console.error('Error loading feature flags:', error);
          this.flagsLoadedSubject.next(true);
          return of({});
        }),
        finalize(() => {
          this.featureFlagsLoad$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    return this.featureFlagsLoad$;
  }

  isFeatureEnabled(featureName: string): boolean {
    const flags = this.featureFlagsSubject.value;
    // Fall back to environment config if flag not found in backend
    return flags[featureName] !== undefined ?
      flags[featureName] :
      this.getEnvironmentFlagValue(featureName);
  }

  /**
   * Get raw flag value (could be boolean or other types) from the latest payload.
   */
  getFlag<T = any>(name: string): T | undefined {
    const flags = this.featureFlagsSubject.value || {};
    return flags[name] as T;
  }

  /**
   * Wait until feature flags have been loaded (or a timeout elapses).
   * If possible, this will trigger loading. Resolves when a load attempt completes,
   * not necessarily when a particular flag exists in the payload.
   */
  waitForFeatureFlags(timeoutMs: number = 7000): Promise<void> {
    return new Promise((resolve) => {
      // If already loaded, resolve immediately
      if (this.flagsLoadedSubject.value) {
        resolve();
        return;
      }

      // Try to ensure dependencies are available to decide if we can load now
      try {
        if (!this.storageService) {
          this.storageService = this.injector.get(StorageService);
        }
        if (!this.companyService) {
          this.companyService = this.injector.get(CompanyService);
        }
        if (!this.authService) {
          this.authService = this.injector.get(AuthenticationService);
        }
      } catch (_) {
        // ignore
      }

      const tryLoadIfPossible = () => {
        this.syncLoginFromToken();
        if (this.isLoggedIn && this.storageService?.companyId) {
          this.loadFeatureFlags().subscribe();
        }
      };

      // Attempt initial load (if we can)
      tryLoadIfPossible();

      // Also listen for company change to load once available
      const companySub = this.companyService?.currentCompanyChanged$?.subscribe(() => tryLoadIfPossible());

      // Resolve when flagsLoaded becomes true or when timeout elapses
      const flagsSub = this.flagsLoaded$.subscribe((loaded) => {
        if (loaded) {
          cleanup();
          resolve();
        }
      });

      const timeout = setTimeout(() => {
        cleanup();
        resolve();
      }, timeoutMs);

      const cleanup = () => {
        clearTimeout(timeout);
        flagsSub?.unsubscribe();
        companySub?.unsubscribe();
      };
    });
  }

  private getEnvironmentFlagValue(featureName: string): boolean {
    // This is a fallback to the current environment-based system
    switch (featureName) {
      case 'receivables':
        return false;
      case 'payables_payment_terms':
        return false;
      case 'ai_forecast_health':
        return false;
      case 'ebics':
        return false;
      case MCP_SERVER_ADDON_FLAG:
        return false;
      default:
        return false;
    }
  }
}
